import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  BusinessFunction,
  DerivedContext,
} from '../snapshot/data-views.ts';

import * as ops from './platform-map-draft.ts';
import type { PlatformMapFramework } from './platform-map-draft.ts';

export type {
  DraftSkill,
  DraftDerivedContext,
  DraftBusinessFunction,
  PlatformMapFramework,
} from './platform-map-draft.ts';

/**
 * Local, unsaved draft of an org's platform map. Seeds from the fetched
 * catalog and applies the pure {@link ops} in {@link platform-map-draft}
 * to in-memory state; nothing hits the network until "Save & Rerun"
 * (PR3). This hook owns the React state, fresh-id minting, and dirty
 * tracking; all the actual edit logic lives in the pure module.
 */
export interface PlatformMapDraft {
  framework: PlatformMapFramework;
  dirty: boolean;
  reset: () => void;
  renameDerivedContext: (id: string, display: string) => void;
  deleteDerivedContext: (id: string) => void;
  /** Returns the minted id so the caller can auto-open it for inline edit. */
  addDerivedContext: (display: string) => string;
  renameSkill: (compId: string, skillId: string, display: string) => void;
  deleteSkill: (compId: string, skillId: string) => void;
  /** Returns the minted id so the caller can auto-open it for inline edit. */
  addSkill: (compId: string, display: string) => string;
  renameBusinessFunction: (id: string, display: string) => void;
  editBusinessFunctionDescription: (id: string, subtitle: string) => void;
  deleteBusinessFunction: (id: string) => void;
  /** Returns the minted id so the caller can auto-open it for inline edit. */
  addBusinessFunction: (display: string) => string;
  /** Set (or clear, when `parentId` is null) a function's parent for sub-grouping. */
  setBusinessFunctionParent: (id: string, parentId: string | null) => void;
  toggleIntersection: (compId: string, fnId: string) => void;
}

export function usePlatformMapDraft(
  derivedContexts: DerivedContext[],
  functions: BusinessFunction[],
): PlatformMapDraft {
  const [framework, setFramework] = useState<PlatformMapFramework>(() =>
    ops.seedFramework(derivedContexts, functions),
  );
  const [dirty, setDirty] = useState(false);
  const counter = useRef(0);
  const mintId = useCallback((kind: string) => {
    counter.current += 1;
    return `local-${kind}-${counter.current}`;
  }, []);

  // Re-seed from the server data whenever it changes, but only while the
  // user has no unsaved edits — so a background refetch can't clobber an
  // in-progress draft.
  useEffect(() => {
    if (!dirty) setFramework(ops.seedFramework(derivedContexts, functions));
  }, [derivedContexts, functions, dirty]);

  const apply = useCallback(
    (fn: (prev: PlatformMapFramework) => PlatformMapFramework) => {
      setDirty(true);
      setFramework(fn);
    },
    [],
  );

  return useMemo<PlatformMapDraft>(
    () => ({
      framework,
      dirty,
      reset: () => {
        setDirty(false);
        setFramework(ops.seedFramework(derivedContexts, functions));
      },
      renameDerivedContext: (id, display) =>
        apply((fw) => ops.renameDerivedContext(fw, id, display)),
      deleteDerivedContext: (id) => apply((fw) => ops.deleteDerivedContext(fw, id)),
      addDerivedContext: (display) => {
        const id = mintId('comp');
        apply((fw) => ops.addDerivedContext(fw, id, display));
        return id;
      },
      renameSkill: (compId, skillId, display) =>
        apply((fw) => ops.renameSkill(fw, compId, skillId, display)),
      deleteSkill: (compId, skillId) =>
        apply((fw) => ops.deleteSkill(fw, compId, skillId)),
      addSkill: (compId, display) => {
        const id = mintId('skill');
        apply((fw) => ops.addSkill(fw, compId, id, display));
        return id;
      },
      renameBusinessFunction: (id, display) =>
        apply((fw) => ops.renameBusinessFunction(fw, id, display)),
      editBusinessFunctionDescription: (id, subtitle) =>
        apply((fw) => ops.editBusinessFunctionDescription(fw, id, subtitle)),
      deleteBusinessFunction: (id) =>
        apply((fw) => ops.deleteBusinessFunction(fw, id)),
      addBusinessFunction: (display) => {
        const id = mintId('bizfn');
        apply((fw) => ops.addBusinessFunction(fw, id, display));
        return id;
      },
      setBusinessFunctionParent: (id, parentId) =>
        apply((fw) => ops.setBusinessFunctionParent(fw, id, parentId)),
      toggleIntersection: (compId, fnId) =>
        apply((fw) => ops.toggleIntersection(fw, compId, fnId)),
    }),
    [framework, dirty, derivedContexts, functions, apply, mintId],
  );
}
